/**
 * Lightweight right-click context menu. Renders a floating menu at a given
 * (x, y) screen coordinate, dismisses on click outside or Escape.
 *
 * Not using Radix ContextMenu since we'd need to wrap every appointment
 * block in a separate ContextMenuTrigger. With our absolute-positioned
 * blocks and a single page-level menu, a small custom component is simpler.
 */
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * ⭐ EXPORTED 2026-09-09. The driver card builds a menu too now (Pete: *"Why
 * can't you give me a dropdown too that has the same things as when you right
 * click an appointment??????? So i have all options????"*), and it CONCATENATES
 * the appointment's own items rather than re-listing them.
 *
 * ⛔ ONE DEFINITION OF THE APPOINTMENT MENU, IN Calendar.tsx. A second list on
 * the driver card would drift the first time an entry was added to either — and
 * the whole complaint was that the card offered less than the right-click did.
 */
export interface MenuItem {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    /** Show as red/destructive */
    destructive?: boolean;
    /** Greyed out, not clickable */
    disabled?: boolean;
}

interface ContextMenuProps {
    /** Screen coordinate where the menu's top-left should appear */
    x: number;
    y: number;
    items: MenuItem[];
    onClose: () => void;
}

export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        // Defer attaching mousedown listener so the current right-click event
        // that opened the menu doesn't immediately close it
        const id = setTimeout(() => {
            window.addEventListener('mousedown', onDocMouseDown);
        }, 0);
        window.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(id);
            window.removeEventListener('mousedown', onDocMouseDown);
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    // Clamp to viewport — don't render off the right or bottom edge
    const clampedX = Math.min(x, window.innerWidth - 220);
    const clampedY = Math.min(y, window.innerHeight - items.length * 36 - 16);

    return (
        <div
            ref={ref}
            role="menu"
            className="fixed z-[60] min-w-[200px] rounded-md border bg-card shadow-lg py-1 animate-fade-in"
            style={{ left: clampedX, top: clampedY }}
        >
            {items.map((item, i) => (
                <button
                    key={i}
                    role="menuitem"
                    onClick={() => {
                        if (item.disabled) return;
                        item.onClick();
                        onClose();
                    }}
                    disabled={item.disabled}
                    className={cn(
                        'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                        item.disabled
                            ? 'text-muted-foreground cursor-not-allowed'
                            : item.destructive
                              ? 'text-destructive hover:bg-destructive/10'
                              : 'hover:bg-accent hover:text-accent-foreground',
                    )}
                >
                    {item.icon && <span className="shrink-0">{item.icon}</span>}
                    <span>{item.label}</span>
                </button>
            ))}
        </div>
    );
}
